import type { GlobalAiCustomDashAssistContext } from './global-ai-assistant-context';

const UNSAVED_CHANGES_CONFIRM_MESSAGE =
  'You have unsaved changes that will be lost. Do you want to leave this page?';

const getPathnameFromRoute = (path: string) => path.split(/[?#]/)[0] || '/';

type ConfirmGlobalAiAssistantRedirectArgs = {
  customDashContext: GlobalAiCustomDashAssistContext;
  currentPathname: string;
  targetPath?: string;
  confirmLeave?: (message: string) => boolean;
};

type ConfirmGlobalAiAssistantRedirectResult =
  | { shouldContinue: false; skipGuard: false; discardChanges?: undefined }
  | { shouldContinue: true; skipGuard: false; discardChanges?: undefined }
  | { shouldContinue: true; skipGuard: true; discardChanges: () => void };

export function getGlobalAiAssistantRoutePathname(path: string) {
  return getPathnameFromRoute(path);
}

export function confirmGlobalAiAssistantRedirect({
  customDashContext,
  currentPathname,
  targetPath,
  confirmLeave = (message) => window.confirm(message),
}: ConfirmGlobalAiAssistantRedirectArgs): ConfirmGlobalAiAssistantRedirectResult {
  const shouldConfirm = (() => {
    if (
      !customDashContext.isInCustomDashEditMode ||
      !customDashContext.hasUnsavedChanges
    ) {
      return false;
    }
    if (!targetPath) {
      return true;
    }

    const targetPathname = getPathnameFromRoute(targetPath);
    if (targetPathname === currentPathname) {
      return false;
    }

    const normalize = (pathname: string) => pathname.replace(/\/edit$/, '');
    // Mirrors useNavigationGuard: leaving an edit URL should prompt even
    // when the target is the same page's non-edit URL.
    return (
      normalize(currentPathname) !== normalize(targetPathname) ||
      currentPathname.endsWith('/edit')
    );
  })();

  if (!shouldConfirm) {
    return { shouldContinue: true, skipGuard: false };
  }

  const didConfirm = confirmLeave(UNSAVED_CHANGES_CONFIRM_MESSAGE);
  if (!didConfirm) {
    return { shouldContinue: false, skipGuard: false };
  }

  return customDashContext.discardChanges
    ? {
        shouldContinue: true,
        skipGuard: true,
        discardChanges: customDashContext.discardChanges,
      }
    : { shouldContinue: true, skipGuard: false };
}
